/*
 * Activity and sign-in logs, and who is online — for both the company
 * (tenant/...) and the platform admin (admin/...). Same endpoints and shapes
 * on both sides (Tenant|Admin ActivityLogController, AuthLogController).
 */
import { users, tenants } from "@/mock/admin";
import { ok, notFound, paginate, hoursAgo } from "./util";

const AGENT = "Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/129.0 Safari/537.36";
const MOBILE = "Mozilla/5.0 (iPhone; CPU iPhone OS 17_6 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.6 Mobile/15E148 Safari/604.1";

const person = (id) => users.find((u) => u.id === id);

// One activity row, with the columns activity_logs has.
const activity = (n, who, action_type, module, submodule, description, method, path, hours, request_payload = null, response_payload = null) => ({
  id: `a${String(n).padStart(4, "0")}-6f1c-4e1b-9a7e-${String(n).padStart(12, "0")}`,
  actor_id: who?.id ?? null,
  actor_name: who?.name ?? null,
  actor_email: who?.email ?? null,
  impersonated_by_admin_id: null,
  action_type, module, submodule, description,
  resource_id: null,
  method, path,
  route_name: path.replace(/^api\/v1\//, "").replace(/\//g, ".").replace(/\.\d+/g, ""),
  status_code: action_type === "create" ? 201 : 200,
  request_payload, response_payload,
  ip_address: `197.248.${10 + (n % 40)}.${20 + (n * 7) % 200}`,
  user_agent: n % 4 === 0 ? MOBILE : AGENT,
  duration_ms: 40 + (n * 37) % 400,
  created_at: hoursAgo(hours),
  deleted_at: null,
});

const tenantActivity = [
  activity(1, person(3), "create", "Sales", "Prospects", "Created prospect Kilimo Fresh Exporters (PRS-0101)", "POST", "api/v1/tenant/prospects", 0.4, { name: "Kilimo Fresh Exporters", industry: "Agriculture", source: "Referral" }, { id: 101, number: "PRS-0101" }),
  activity(2, person(1), "update", "Sales", "Opportunities", "Moved OPP-0311 Lakeview SACCO to Proposal", "PUT", "api/v1/tenant/opportunities/311", 1.2, { stage: "proposal" }),
  activity(3, person(2), "update", "Sales", "Quotations", "Approved quotation QUO-2026-0151 (discount 8%)", "POST", "api/v1/tenant/quotations/151/approve", 2.5, { comments: "Within limit" }),
  activity(4, person(8), "update", "Support", "Tickets", "Closed ticket TKT-0401 (resolved: configuration)", "PUT", "api/v1/tenant/tickets/401", 3.1, { status: "closed", resolution: "Configuration corrected" }),
  activity(5, person(7), "create", "Customer Success", "Health reviews", "Logged quarterly review for Harambee Sacco Society", "POST", "api/v1/tenant/success/reviews", 5),
  activity(6, person(11), "update", "Administration", "Roles", "Proposed adding quotations.approve to Finance", "POST", "api/v1/tenant/role-permissions/7/propose", 8, { add: ["quotations.approve"], remove: [] }),
  activity(7, person(1), "view-all", "Sales", "Leads", "Viewed the leads list", "GET", "api/v1/tenant/leads", 9),
  activity(8, person(6), "update", "Onboarding", "Projects", "Marked data migration complete for Pwani Logistics", "PUT", "api/v1/tenant/onboarding/46", 20),
  activity(9, person(9), "create", "Marketing", "Campaigns", "Created campaign HR Summit 2026 follow-up", "POST", "api/v1/tenant/campaigns", 26),
  activity(10, person(4), "create", "Sales", "Leads", "Converted prospect PRS-0098 to lead LEA-0640", "POST", "api/v1/tenant/prospects/98/convert", 30),
  activity(11, person(2), "view-one", "Sales", "Contracts", "Opened contract CON-0218 Savannah Hotels Group", "GET", "api/v1/tenant/contracts/218", 33),
  activity(12, person(11), "update", "Finance", "Renewals", "Raised renewal invoice for Savannah Hotels Group", "POST", "api/v1/tenant/renewals/77/invoice", 49),
  activity(13, person(1), "update", "Administration", "Role assignments", "Proposed Joseph Mutiso for the Support role", "POST", "api/v1/tenant/role-assignments/employee/14/propose", 52),
  activity(14, person(7), "update", "Retention", "Churn", "Flagged Kampala Retail Hub as at-risk (usage drop)", "PUT", "api/v1/tenant/churn/12", 70),
  activity(15, person(5), "delete", "Sales", "Activities", "Deleted a duplicate follow-up call", "DELETE", "api/v1/tenant/activities/882", 75),
  activity(16, person(13), "view-all", "Reports", "Revenue", "Viewed the revenue by product report", "GET", "api/v1/tenant/reports/revenue", 96),
  activity(17, person(3), "update", "Sales", "Prospects", "Reassigned 4 prospects from Tom Barasa to Peter Ouma", "POST", "api/v1/tenant/prospects/reassign", 120),
  activity(18, person(1), "create", "Data", "Exports", "Requested an export of Roles", "POST", "api/v1/tenant/exports", 150),
  activity(19, person(8), "create", "Support", "Tickets", "Ticket TKT-0399 received from Harambee member portal (API)", "POST", "api/v1/tickets", 170),
  activity(20, person(2), "update", "Sales", "Quotations", "Returned quotation QUO-2026-0149 for a revised discount", "POST", "api/v1/tenant/quotations/149/return", 190),
];

const PLATFORM = [
  { id: 1, name: "Platform Admin", email: "demo@deecrm360.test" },
  { id: 2, name: "Kevin Otieno", email: "kevin.otieno@deecrm360.com" },
  { id: 3, name: "Mary Achieng", email: "mary.achieng@deecrm360.com" },
];
const t = (id) => tenants.find((x) => x.id === id)?.name ?? "a company";

const adminActivity = [
  activity(101, PLATFORM[1], "create", "Companies", "Tenants", `Created company ${t(3)} (CRM003)`, "POST", "api/v1/admin/tenants", 1, { name: t(3), plan: "starter" }),
  activity(102, PLATFORM[2], "update", "Companies", "Tenants", `Changed ${t(1)} plan Starter → Business`, "PUT", "api/v1/admin/tenants/1", 4),
  activity(103, PLATFORM[0], "update", "Companies", "Tenants", `Suspended ${t(4)}: invoice 20 days overdue`, "POST", "api/v1/admin/tenants/4/suspend", 7),
  activity(104, PLATFORM[1], "update", "Sales", "Trial applications", "Approved trial application from Mvuli Insurance Brokers", "POST", "api/v1/admin/trial-applications/12/approve", 22),
  activity(105, PLATFORM[2], "update", "Billing", "Plans", "Updated plan Enterprise: added WhatsApp channel", "PUT", "api/v1/admin/plans/3", 30),
  activity(106, PLATFORM[0], "create", "Companies", "Sign-in addresses", `Added address crm.harambeesacco.co.ke for ${t(2)}`, "POST", "api/v1/admin/tenants/2/addresses", 50),
  activity(107, PLATFORM[1], "view-all", "Usage", "Client usage", "Viewed client usage for the last 30 days", "GET", "api/v1/admin/client-usage", 54),
  activity(108, PLATFORM[2], "create", "Administration", "Administrators", "Invited administrator support@deecrm360.com", "POST", "api/v1/admin/admins", 80),
  activity(109, PLATFORM[0], "update", "Catalogue", "Modules", "Renamed module Support to Support & Ticket API", "PUT", "api/v1/admin/catalogue/modules/7", 140),
  activity(110, PLATFORM[1], "update", "Website", "Settings", "Updated website contact phone", "PUT", "api/v1/admin/website-settings", 200),
];

// One sign-in row, with the columns auth_logs has.
const auth = (n, email, name, action_type, hours, failure_reason = null, tenant_code = null) => ({
  id: `b${String(n).padStart(4, "0")}-2d4e-4c7a-8b1f-${String(n).padStart(12, "0")}`,
  actor_id: action_type === "login-failed" ? null : n,
  actor_name: action_type === "login-failed" ? null : name,
  attempted_email: email,
  attempted_tenant_code: tenant_code,
  action_type, failure_reason,
  roles: null,
  ip_address: `41.90.${60 + (n % 30)}.${10 + (n * 11) % 230}`,
  user_agent: n % 3 === 0 ? MOBILE : AGENT,
  created_at: hoursAgo(hours),
  deleted_at: null,
});

const tenantAuth = [
  auth(1, "jane.wanjiku@demulla.co.ke", "Jane Wanjiku", "login-success", 0.2, null, "CRM001"),
  auth(2, "dennis.kiprop@demulla.co.ke", null, "login-failed", 0.9, "Invalid credentials", "CRM001"),
  auth(3, "dennis.kiprop@demulla.co.ke", "Dennis Kiprop", "login-success", 0.8, null, "CRM001"),
  auth(4, "faith.achieng@demulla.co.ke", "Faith Achieng", "login-success", 2, null, "CRM001"),
  auth(5, "brian.otieno@demulla.co.ke", "Brian Otieno", "logout", 4, null, "CRM001"),
  auth(6, "samuel.kariuki@demulla.co.ke", null, "login-failed", 6, "Account locked after 5 failed attempts", "CRM001"),
  auth(7, "ann.mutua@demulla.co.ke", "Ann Mutua", "auto-logout", 20, null, "CRM001"),
  auth(8, "tom.barasa@demulla.co.ke", null, "login-failed", 30, "Account deactivated", "CRM001"),
  auth(9, "mercy.wambui@demulla.co.ke", "Mercy Wambui", "login-success", 26, null, "CRM001"),
  auth(10, "wanjiru.kamau@demulla.co.ke", "Wanjiru Kamau", "login-success", 216, null, "CRM001"),
  auth(11, "peter.ouma@demulla.co.ke", null, "login-failed", 50, "Invalid credentials", "CRM001"),
  auth(12, "grace.njeri@demulla.co.ke", "Grace Njeri", "logout", 52, null, "CRM001"),
];

const adminAuth = [
  auth(101, "demo@deecrm360.test", "Platform Admin", "login-success", 0.1),
  auth(102, "kevin.otieno@deecrm360.com", "Kevin Otieno", "login-success", 3),
  auth(103, "mary.achieng@deecrm360.com", null, "login-failed", 5, "Invalid credentials"),
  auth(104, "mary.achieng@deecrm360.com", "Mary Achieng", "login-success", 4.9),
  auth(105, "kevin.otieno@deecrm360.com", "Kevin Otieno", "auto-logout", 9),
  auth(106, "unknown@example.com", null, "login-failed", 40, "Invalid credentials"),
];

// Who has a live session; online means active within the session timeout.
const tenantStatus = users
  .filter((u) => u.status === "active" && u.last_login)
  .map((u, i) => ({ user_id: u.id, name: u.name, email: u.email, status: i % 3 === 0 ? "online" : "offline", last_active: hoursAgo(i % 3 === 0 ? 0.1 : 2 + i * 5) }));
const adminStatus = PLATFORM.map((a, i) => ({ user_id: a.id, name: a.name, email: a.email, status: i < 2 ? "online" : "offline", last_active: hoursAgo(i < 2 ? 0.05 : 9) }));

const dateOf = (row) => row.created_at.slice(0, 10);

const listActivity = (rows) => ({ query }) =>
  ok(paginate(
    rows
      .filter((r) => (!query.actor_id || String(r.actor_id) === String(query.actor_id))
        && (!query.actor_email || String(r.actor_email ?? "").toLowerCase().includes(String(query.actor_email).toLowerCase()))
        && (!query.action_type || r.action_type === query.action_type)
        && (!query.module || r.module === query.module)
        && (!query.submodule || r.submodule === query.submodule)
        && (!query.date_from || dateOf(r) >= query.date_from)
        && (!query.date_to || dateOf(r) <= query.date_to))
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    query,
    30,
  ));

const listAuth = (rows) => ({ query }) =>
  ok(paginate(
    rows
      .filter((r) => (!query.actor_id || String(r.actor_id) === String(query.actor_id))
        && (!query.attempted_email || r.attempted_email.toLowerCase().includes(String(query.attempted_email).toLowerCase()))
        && (!query.action_type || r.action_type === query.action_type)
        && (!query.date_from || dateOf(r) >= query.date_from)
        && (!query.date_to || dateOf(r) <= query.date_to))
      .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    query,
    30,
  ));

const showFrom = (rows) => ({ params }) => {
  const row = rows.find((r) => r.id === params.id);
  return row ? ok(row) : notFound("Log entry not found");
};

const statusOf = (rows) => ({ query }) => ok(query.status ? rows.filter((r) => r.status === query.status) : rows);

export default [
  ["get", "tenant/logs", listActivity(tenantActivity)],
  ["get", "tenant/logs/:id", showFrom(tenantActivity)],
  ["get", "tenant/logs-user-status", statusOf(tenantStatus)],
  ["get", "tenant/auth-logs", listAuth(tenantAuth)],
  ["get", "tenant/auth-logs/:id", showFrom(tenantAuth)],

  ["get", "admin/logs", listActivity(adminActivity)],
  ["get", "admin/logs/:id", showFrom(adminActivity)],
  ["get", "admin/logs-user-status", statusOf(adminStatus)],
  ["get", "admin/auth-logs", listAuth(adminAuth)],
  ["get", "admin/auth-logs/:id", showFrom(adminAuth)],
];
